import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { FaUser, FaLock, FaEye, FaEyeSlash } from "react-icons/fa";
import AdminLoader from "../../components/common/loader/AdminLoader";
import LockoutCountdown from "../../components/common/LockoutCountdown";
import { useDispatch, useSelector } from "react-redux";
import {
  clearPendingVerification,
  clearOtpLockout,
  incrementLoginAttempts,
  resetLoginAttempts,
  resendLoginOtp,
  tryLogin,
  verifyLoginOtp,
} from "../../store/slices/sessionSlice";
import { unwrapResult } from "@reduxjs/toolkit";
import {
  clearLockoutFromSession,
  formatCountdown,
  isAccountLockout,
  loadLockoutFromSession,
  parseLockoutFromPayload,
  saveLockoutToSession,
} from "../../utils/lockoutUtils";
import logo from "../../assets/peopleskillLogo.svg";

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  return error?.message || error?.error || error?.msg || fallback;
};

const isNetworkError = (error) =>
  !error?.status && !error?.lockedUntil && !error?.remainingAttempts;

// Stable constant so React hook dependency checks don't warn.
const RESEND_OTP_SECONDS = 5 * 60;

// OTP login temporarily disabled — set to true to re-enable verify-email step.
const OTP_LOGIN_ENABLED = false;

const LockoutBanner = ({ lockout, onLockoutExpire }) => (
  <div
    className="mb-4 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-left text-sm text-amber-900"
    role="alert"
  >
    <p className="font-semibold">
      Your account is temporarily locked for security. Please wait before trying
      again.
    </p>
    <p className="mt-1">
      Too many failed attempts. Try again in{" "}
      <LockoutCountdown
        lockedUntil={lockout.lockedUntil}
        retryAfterSeconds={lockout.retryAfterSeconds}
        onExpire={onLockoutExpire}
        className="font-semibold tabular-nums"
      />
      .
    </p>
  </div>
);

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const loginAttempts = useSelector((state) => state.session.loginAttempts);
  const pendingVerification = useSelector(
    (state) => state.session.pendingVerification
  );
  const otpLockout = useSelector((state) => state.session.otpLockout);
  const [email, setEmail] = useState(pendingVerification?.email || "");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [step, setStep] = useState(
    OTP_LOGIN_ENABLED && pendingVerification ? "otp" : "credentials"
  );
  const [verificationId, setVerificationId] = useState(
    pendingVerification?.verificationId || ""
  );
  const [maskedEmail, setMaskedEmail] = useState(
    pendingVerification?.maskedEmail || ""
  );
  const [otp, setOtp] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(RESEND_OTP_SECONDS);
  const [lockout, setLockout] = useState(null);
  const [otpError, setOtpError] = useState("");
  const lockoutEmailRef = useRef(email);

  const isLocked = Boolean(lockout);

  const togglePassword = () => {
    setPasswordVisible(!passwordVisible);
  };

  const handleMouseEnter = () => {
    setPasswordVisible(true);
  };

  const handleMouseLeave = () => {
    setPasswordVisible(false);
  };

  const clearLockoutState = useCallback((targetEmail) => {
    if (targetEmail) {
      clearLockoutFromSession(targetEmail);
    }
    setLockout(null);
  }, []);

  const applyLockout = useCallback((targetEmail, lockoutData) => {
    if (!lockoutData) return;
    const resolvedEmail = (targetEmail || lockoutEmailRef.current || "").trim();
    if (resolvedEmail) {
      lockoutEmailRef.current = resolvedEmail;
      saveLockoutToSession(resolvedEmail, lockoutData);
    }
    setLockout(lockoutData);
    setOtpError("");
    setIsLoading(false);
    setIsVerifying(false);
    setIsResending(false);
  }, []);

  const handleLockoutExpire = useCallback(() => {
    clearLockoutState(lockoutEmailRef.current);
    dispatch(clearOtpLockout());
  }, [clearLockoutState, dispatch]);

  const handleLockoutFromError = useCallback(
    (errorPayload, targetEmail) => {
      const lockoutData = parseLockoutFromPayload(errorPayload);
      if (lockoutData) {
        applyLockout(targetEmail, lockoutData);
        return true;
      }
      return false;
    },
    [applyLockout]
  );

  const restoreLockoutForEmail = useCallback((targetEmail) => {
    if (!targetEmail) return;
    const storedLockout = loadLockoutFromSession(targetEmail);
    if (storedLockout) {
      lockoutEmailRef.current = targetEmail;
      setLockout({
        lockedUntil: storedLockout.lockedUntil,
        retryAfterSeconds: storedLockout.retryAfterSeconds,
      });
    }
  }, []);

  const handleEmailChange = useCallback(
    (event) => {
      const nextEmail = event.target.value;
      if (nextEmail.trim().toLowerCase() !== email.trim().toLowerCase()) {
        setLockout(null);
        setOtpError("");
      }
      setEmail(nextEmail);
    },
    [email]
  );

  const loginFailureCallback = useCallback(() => {
    dispatch(incrementLoginAttempts());
    setIsLoading(false);
  }, [dispatch]);

  const otpFailureCallback = useCallback(() => {
    setIsLoading(false);
    enqueueSnackbar("Invalid OTP!", { variant: "error" });
  }, [enqueueSnackbar]);

  const loginSuccessCallback = useCallback(
    async () => {
      setIsLoading(false);
      clearLockoutState(email);
      dispatch(clearOtpLockout());
      navigate(`/dashboard/`);
      dispatch(resetLoginAttempts());
      enqueueSnackbar("Logged in successfully!", {
        variant: "success",
      });
    },
    [navigate, enqueueSnackbar, dispatch, email, clearLockoutState]
  );

  const handleBackToLogin = useCallback(() => {
    dispatch(clearPendingVerification());
    setStep("credentials");
    setOtp("");
    setVerificationId("");
    setMaskedEmail("");
    setOtpError("");
    setIsLoading(false);
  }, [dispatch]);

  const verificationEmail = pendingVerification?.email || email;
  const activeVerificationId =
    verificationId || pendingVerification?.verificationId || "";
  const activeMaskedEmail =
    maskedEmail ||
    pendingVerification?.maskedEmail ||
    pendingVerification?.email ||
    "";

  const handleLogin = useCallback(async () => {
    if (isLocked) return;

    if (!email) {
      enqueueSnackbar("Please Enter Email", { variant: "error" });
    } else if (!password) {
      enqueueSnackbar("Please Enter Password", { variant: "error" });
    } else {
      try {
        setIsLoading(true);
        setOtpError("");
        const resultAction = await dispatch(
          tryLogin({
            email,
            password,
            loginSuccessCallback,
            loginFailureCallback,
          })
        );
        const result = unwrapResult(resultAction);
        const needsVerification =
          result?.requiresVerification ||
          (result?.verificationId && !result?.token);

        if (needsVerification && OTP_LOGIN_ENABLED) {
          setVerificationId(result.verificationId || "");
          setMaskedEmail(result.email || email);
          setStep("otp");
          setOtp("");
          setIsLoading(false);
          enqueueSnackbar(
            result.message || "Verification code sent to your email.",
            { variant: "success" }
          );
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
        if (handleLockoutFromError(error, email)) {
          return;
        }
        if (isNetworkError(error)) {
          enqueueSnackbar("Something went wrong. Please try again.", {
            variant: "error",
          });
          return;
        }
        if (!isAccountLockout(error)) {
          enqueueSnackbar(getErrorMessage(error, "Invalid credentials"), {
            variant: "error",
          });
        }
      }
    }
  }, [
    email,
    password,
    enqueueSnackbar,
    dispatch,
    loginSuccessCallback,
    loginFailureCallback,
    isLocked,
    handleLockoutFromError,
  ]);

  const handleVerifyOtp = useCallback(async () => {
    if (isLocked) return;

    if (!otp || otp.trim().length < 6) {
      enqueueSnackbar("Please enter the 6-digit verification code", {
        variant: "error",
      });
      return;
    }

    const lockoutEmail = verificationEmail || email;

    try {
      setIsVerifying(true);
      setOtpError("");
      const resultAction = await dispatch(
        verifyLoginOtp({
          verificationId: activeVerificationId,
          otp: otp.trim(),
          email: verificationEmail,
          loginSuccessCallback,
          loginFailureCallback: otpFailureCallback,
        })
      );

      const error = resultAction.payload;
      const gotLockout =
        verifyLoginOtp.rejected.match(resultAction) &&
        handleLockoutFromError(error, lockoutEmail);

      if (gotLockout) {
        setIsVerifying(false);
        return;
      }

      if (verifyLoginOtp.fulfilled.match(resultAction)) {
        if (handleLockoutFromError(error, lockoutEmail)) {
          setIsVerifying(false);
          return;
        }
        if (!error?.token) {
          setIsVerifying(false);
          enqueueSnackbar("Invalid OTP!", { variant: "error" });
          return;
        }
        setIsVerifying(false);
        return;
      }

      setIsVerifying(false);
      if (isNetworkError(error)) {
        enqueueSnackbar("Something went wrong. Please try again.", {
          variant: "error",
        });
        return;
      }
      if (error?.remainingAttempts != null) {
        if (
          error.remainingAttempts === 0 &&
          handleLockoutFromError(error, lockoutEmail)
        ) {
          return;
        }
        setOtpError(
          `Invalid code. ${error.remainingAttempts} attempt${
            error.remainingAttempts === 1 ? "" : "s"
          } remaining.`
        );
        return;
      }
      setOtpError(getErrorMessage(error, "Invalid code. Please try again."));
    } catch (error) {
      setIsVerifying(false);
      enqueueSnackbar("Something went wrong. Please try again.", {
        variant: "error",
      });
    }
  }, [
    otp,
    activeVerificationId,
    verificationEmail,
    email,
    dispatch,
    loginSuccessCallback,
    otpFailureCallback,
    enqueueSnackbar,
    isLocked,
    handleLockoutFromError,
  ]);

  const handleResendOtp = useCallback(async () => {
    if (isLocked) return;

    if (!activeVerificationId || !verificationEmail) {
      enqueueSnackbar("Unable to resend code. Please login again.", {
        variant: "error",
      });
      handleBackToLogin();
      return;
    }

    try {
      setIsResending(true);
      setOtpError("");
      const resultAction = await dispatch(
        resendLoginOtp({
          verificationId: activeVerificationId,
          email: verificationEmail,
        })
      );
      const result = unwrapResult(resultAction);
      if (result?.verificationId) {
        setVerificationId(result.verificationId);
      }
      enqueueSnackbar("Otp has resend to email plz check", {
        variant: "success",
      });
      setResendCountdown(RESEND_OTP_SECONDS);
    } catch (error) {
      if (handleLockoutFromError(error, verificationEmail)) {
        return;
      }
      if (isNetworkError(error)) {
        enqueueSnackbar("Something went wrong. Please try again.", {
          variant: "error",
        });
        return;
      }
      enqueueSnackbar(
        getErrorMessage(error, "Failed to resend verification code."),
        { variant: "error" }
      );
    } finally {
      setIsResending(false);
    }
  }, [
    activeVerificationId,
    verificationEmail,
    dispatch,
    enqueueSnackbar,
    handleBackToLogin,
    isLocked,
    handleLockoutFromError,
  ]);

  useEffect(() => {
    if (!otpLockout) return;
    const lockoutEmail =
      otpLockout.email || pendingVerification?.email || email;
    if (!lockoutEmail) return;
    applyLockout(lockoutEmail, {
      lockedUntil: otpLockout.lockedUntil,
      retryAfterSeconds: otpLockout.retryAfterSeconds,
    });
  }, [otpLockout, pendingVerification?.email, email, applyLockout]);

  useEffect(() => {
    if (isLoading || isVerifying || step !== "otp") return;
    const accountEmail = pendingVerification?.email || email;
    if (!accountEmail) return;
    const storedLockout = loadLockoutFromSession(accountEmail);
    if (storedLockout) {
      lockoutEmailRef.current = accountEmail;
      setLockout({
        lockedUntil: storedLockout.lockedUntil,
        retryAfterSeconds: storedLockout.retryAfterSeconds,
      });
    }
  }, [isLoading, isVerifying, step, email, pendingVerification?.email]);

  useEffect(() => {
    restoreLockoutForEmail(pendingVerification?.email || email);
  }, [email, pendingVerification?.email, restoreLockoutForEmail]);

  useEffect(() => {
    if (!OTP_LOGIN_ENABLED) {
      setStep("credentials");
      return;
    }
    if (pendingVerification) {
      setStep("otp");
      setVerificationId(pendingVerification.verificationId || "");
      setMaskedEmail(
        pendingVerification.maskedEmail || pendingVerification.email || ""
      );
      if (pendingVerification.email) {
        setEmail(pendingVerification.email);
      }
      setIsLoading(false);
      setResendCountdown(RESEND_OTP_SECONDS);
    } else {
      setStep("credentials");
      setOtp("");
      setVerificationId("");
      setMaskedEmail("");
      setResendCountdown(RESEND_OTP_SECONDS);
    }
  }, [pendingVerification]);

  useEffect(() => {
    if (step !== "otp") return;
    if (resendCountdown <= 0) return;

    const timerId = setInterval(() => {
      setResendCountdown((s) => Math.max(0, s - 1));
    }, 1000);

    return () => clearInterval(timerId);
  }, [step, resendCountdown]);

  useEffect(() => {
    if (step !== "credentials") return;

    if (loginAttempts >= 3) {
      enqueueSnackbar(
        "Too many failed attempts. Redirecting to Forgot Password...",
        { variant: "error" }
      );
      setTimeout(() => {
        dispatch(resetLoginAttempts());
        navigate("/forgotpw");
      }, 3000);
    } else if (loginAttempts > 0) {
      enqueueSnackbar("Invalid credentials", {
        variant: "error",
        autoHideDuration: 3000,
      });
      setIsLoading(false);
    }
  }, [enqueueSnackbar, loginAttempts, navigate, dispatch, step]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "Enter" && !isLoading && !isVerifying && !isResending && !isLocked) {
        if (step === "credentials") {
          handleLogin();
        } else if (step === "otp" && OTP_LOGIN_ENABLED) {
          handleVerifyOtp();
        }
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [
    step,
    handleLogin,
    handleVerifyOtp,
    isLoading,
    isVerifying,
    isResending,
    isLocked,
  ]);

  const forgotpw = () => {
    navigate("/forgotpw", {
      state: { email: email },
    });
  };

  return (
    <div className="min-h-screen flex items-center bg-gray-100 justify-center px-4">
      {isLoading && (step === "credentials" || !OTP_LOGIN_ENABLED) && !lockout ? (
        <AdminLoader isLoading={isLoading} />
      ) : (
        <div className="w-full max-w-md md:max-w-2xl lg:px-48 text-center my-12 sm:my-20 px-6 sm:px-10 py-8 sm:py-12 bg-[#fbfafb] overflow-hidden shadow-2xl rounded-lg border border-gray-200">
          <h1 className="text-3xl sm:text-5xl font-extrabold">
            <img src={logo} alt="loginlogo" className="scale-90 mx-auto" />
          </h1>
          <br />

          {step === "credentials" || !OTP_LOGIN_ENABLED ? (
            <>
              <h1 className="font-extrabold text-xl sm:text-2xl">Login</h1>
              <br />

              {isLocked && (
                <LockoutBanner
                  lockout={lockout}
                  onLockoutExpire={handleLockoutExpire}
                />
              )}

              <div className="flex items-center">
                <FaUser size={20} className="text-gray-600" />
                <input
                  className="border ml-2 w-full border-gray-300 rounded-md p-2 shadow-sm outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={email}
                  type="text"
                  placeholder="Enter Email"
                  onChange={handleEmailChange}
                  autoComplete="off"
                  disabled={isLocked}
                />
              </div>

              <br />

              <div className="flex items-center relative">
                <FaLock size={20} className="text-gray-600" />
                <input
                  className="border ml-2 w-full border-gray-300 rounded-md p-2 shadow-sm outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={password}
                  type={passwordVisible ? "text" : "password"}
                  placeholder="Enter Password"
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  disabled={isLocked}
                />
                <div
                  className="absolute right-4 cursor-pointer"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                  onClick={togglePassword}
                  style={{ top: "50%", transform: "translateY(-50%)" }}
                >
                  {passwordVisible ? <FaEye size={18} /> : <FaEyeSlash size={18} />}
                </div>
              </div>

              <h1
                onClick={forgotpw}
                className="text-right mt-2 text-sm underline text-blue-900 hover:text-red-600 cursor-pointer"
              >
                Forgot Password?
              </h1>
              <br />

              <button
                onClick={handleLogin}
                disabled={isLoading || isLocked}
                className={`py-1 text-center mt-2 overflow-hidden shadow-md rounded-sm text-white px-6 
            file: ${
              isLoading || isLocked
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-900"
            }`}
              >
                {isLoading ? "Logging in..." : "Login"}
              </button>
            </>
          ) : (
            <>
              <h1 className="font-extrabold text-xl sm:text-2xl">Verify Email</h1>
              <br />

              {isLocked && (
                <LockoutBanner
                  lockout={lockout}
                  onLockoutExpire={handleLockoutExpire}
                />
              )}

              <p className="text-sm text-gray-600 mb-4">
                Enter the 6-digit code sent to{" "}
                <span className="font-semibold text-gray-800">
                  {activeMaskedEmail}
                </span>
              </p>

              <div className="flex items-center justify-center">
                <input
                  className="border w-full max-w-xs border-gray-300 rounded-md p-2 shadow-sm outline-none text-center tracking-widest text-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
                  value={otp}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter 6-digit code"
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                    if (otpError) setOtpError("");
                  }}
                  autoComplete="one-time-code"
                  disabled={isLocked}
                  aria-invalid={Boolean(otpError)}
                />
              </div>

              {otpError && (
                <p className="mt-2 text-sm text-red-600" role="alert">
                  {otpError}
                </p>
              )}

              <br />

              <button
                onClick={handleVerifyOtp}
                disabled={isVerifying || isLocked}
                className={`py-1 text-center mt-2 overflow-hidden shadow-md rounded-sm text-white px-6 
            file: ${
              isVerifying || isLocked
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-900"
            }`}
              >
                {isVerifying ? "Verifying..." : "Verify"}
              </button>

              <div className="mt-4 flex flex-col items-center gap-2">
                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={isResending || resendCountdown > 0 || isLocked}
                  className={`text-sm underline ${
                    isResending || resendCountdown > 0 || isLocked
                      ? "text-gray-400 cursor-not-allowed no-underline"
                      : "text-blue-900 hover:text-red-600 cursor-pointer"
                  }`}
                >
                  {isResending
                    ? "Resending..."
                    : resendCountdown > 0
                      ? `Resend OTP in ${formatCountdown(resendCountdown)}`
                      : "Resend OTP"}
                </button>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm underline text-blue-900 hover:text-red-600 cursor-pointer"
                >
                  Back to Login
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Login;
